import * as React from 'react';
import { Table, TableBody, TableCell, TableHead, TablePagination, TableRow } from '@mui/material';
import { PurchasesList as PurchasesListDto } from 'src/redux/services/purchases';
import { App } from 'src/dto/App';
import { Purchase, PurchaseSource } from 'src/dto/Credits';
import { Actions } from 'src/pages/BillingAndPlan/components/PaymentHistory/PurchasesList/Actions';
import { fixedDigitsFormat } from 'src/utils/number';
import { getTime, format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import EmptyState from 'src/frontend-common/components/EmptyState';
import { TableWrapper } from 'src/style/common';
import useGetCurrency from 'src/frontend-common/hooks/useGetCurrency';

export interface Props {
  onEdit: (app: Purchase) => void;
  onDelete: (app: Purchase) => void;
  purchases: PurchasesListDto | undefined;
  appKeys: App[];
}

const formatSource = (input: undefined | PurchaseSource): string | undefined => {
  if (!input) {
    return;
  }

  switch (input) {
    case 'INITIAL_CREDIT':
      return 'Initial credits';
    case 'MANUAL':
      return 'Manual funds';
    case 'STRIPE':
      return 'Stripe';
    case 'STRIPE_SUBSCRIPTION':
      return 'Subscription';
  }
};

export const PurchasesList: React.FC<Props> = ({ purchases, onDelete, onEdit, appKeys }) => {
  const [page, setPage] = React.useState(0);
  const rowsPerPage = 10;
  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = () => {
    setPage(0);
  };
  const purchesOrdered = purchases?.items.slice().sort((a, b) => getTime(new Date(b.creation_date)) - getTime(new Date(a.creation_date)));
  const getAppKeyDescriptionById = (app_key: string): string => appKeys.find((a) => a.app_key === app_key)?.name ?? app_key;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    <TableWrapper data-testid="PurchasesList">
      {purchesOrdered && purchesOrdered.length > 0 && (
        <>
          <Table>
            <>
              <TableHead>
                <TableRow>
                  <TableCell>App key</TableCell>
                  <TableCell>Creation date</TableCell>
                  <TableCell>From date</TableCell>
                  <TableCell>Expiration date</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchesOrdered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((a) => (
                  <TableRow key={a.id} hover>
                    <>
                      <TableCell>{getAppKeyDescriptionById(a.app_key)}</TableCell>
                      <TableCell>{format(utcToZonedTime(new Date(a.creation_date), timezone), 'yyyy/MM/dd')}</TableCell>
                      <TableCell>
                        <>{format(utcToZonedTime(new Date(a.activation_date), timezone), 'yyyy/MM/dd')}</>
                      </TableCell>

                      <TableCell>{format(utcToZonedTime(new Date(a.expiration_date), timezone), 'yyyy/MM/dd')}</TableCell>

                      <TableCell>{`${useGetCurrency()} ${fixedDigitsFormat(a.value, 2)}`}</TableCell>

                      <TableCell>{formatSource(a.source)}</TableCell>

                      <Actions model={a} onDelete={onDelete} onEdit={onEdit} />
                    </>
                  </TableRow>
                ))}
              </TableBody>
            </>
          </Table>
          <TablePagination
            component="div"
            style={{ backgroundColor: 'white' }}
            count={purchesOrdered.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[rowsPerPage]}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
      {!purchesOrdered || (purchesOrdered.length === 0 && <EmptyState style={{ padding: '16px 32px', minHeight: 300 }} />)}
    </TableWrapper>
  );
};
